<template>
  <div class="pt-3 pl-5 pr-5 page-fill">
    <div v-if="isLoading" class="flex flex-col flex-1 min-h-0 overflow-hidden">
      <div
        class="row w-full mb-3 justify-between flex"
        style="align-items: center"
      >
        <h1 class="title">KPIs</h1>
        <div class="cardbody">
          <div class="flex flex-row-reverse items-center gap-3">
            <button class="flex" @click="filterOpen" :class="columnObject ? 'filterButtondisabled pointer-events-none' : 'filterButton'">
              <img v-if="columnObject" src="@/assets/filterIcon_Disabled.svg" class="filterIcon"/>
              <img v-else src="@/assets/filter.svg" class="filterIcon" />
              Filters
            </button>
          </div>
        </div>
      </div>
      <div class="text-xs mt-3">
          This page provides a clear view of key performance indicators (KPIs) for the security controls that are adopted or activated. KPIs will help you to monitor effectiveness and gain insights to strengthen your organization's security posture.         </div>
      <div class="mt-5 table-section">
        <tableData
          class="flex-1 min-h-0"
          :tableRowData="tableRow"
          :headersData="headers"
          :departmentArr="deptOptions"
          :loadData="dataLoading"
          columnWidthPreset="kpi"
        ></tableData>
        <div v-if="toolTipActive">
					<tool-tip :tipText="toolTipText"></tool-tip>
				</div>
      </div>
    </div>
    <Teleport to="body">
      <div
        v-if="showPopup"
        class="kpi-history-dialog-overlay"
      >
        <div
          class="kpi-history-dialog"
          :class="{ 'kpi-history-dialog--empty': !kpiResults.length }"
        >
          <div
            v-if="kpiResults.length"
            class="kpi-history-dialog-title flex items-start justify-between px-4"
          >
            <h1 class="kpi-history-dialog-heading">
              <span class="kpi-history-id">{{ allKpis.kpi_id || allKpis.kpiId }}</span>
              <span class="kpi-history-sep">|</span>
              <span class="kpi-history-name">{{ allKpis.description || allKpis.KPI }}</span>
            </h1>
            <button type="button" class="float-right text-3xl" @click="closeKpiHistory">
              <img src="@/assets/cancel.svg" class="h-5 w-5 cursor-pointer" />
            </button>
          </div>
          <div v-if="kpiResults.length" class="border-b border-solid border-lightgrey mx-4"></div>
          <div class="kpi-history-body">
          <div class="kpi-history-popup">
            <div v-if="!kpiResultsLoading && !kpiResults.length" class="kpi-history-empty-wrap">
              <button type="button" class="kpi-history-empty-close" @click="closeKpiHistory">
                <img src="@/assets/cancel.svg" class="h-5 w-5 cursor-pointer" />
              </button>
              <div class="kpi-history-empty-msg">No results found</div>
            </div>
            <div v-else-if="!kpiResultsLoading">
              <div class="kpi-trend-wrap">
                <div class="kpi-trend-header">
                  <h2 class="kpi-trend-title">KPI Target vs Achieved</h2>
                  <div class="kpi-trend-pills">
                    <button
                      type="button"
                      class="kpi-trend-pill"
                      :class="{ 'kpi-trend-pill--active': kpiTrendTimePeriod === '20604' }"
                      @click="setKpiTrendPeriod('20604')"
                    >3M</button>
                    <button
                      type="button"
                      class="kpi-trend-pill"
                      :class="{ 'kpi-trend-pill--active': kpiTrendTimePeriod === '20603' }"
                      @click="setKpiTrendPeriod('20603')"
                    >6M</button>
                    <button
                      type="button"
                      class="kpi-trend-pill"
                      :class="{ 'kpi-trend-pill--active': kpiTrendTimePeriod === '' }"
                      @click="setKpiTrendPeriod('')"
                    >1Y</button>
                    <button
                      type="button"
                      class="kpi-trend-pill"
                      :class="{ 'kpi-trend-pill--active': kpiTrendTimePeriod === '20605' }"
                      @click="setKpiTrendPeriod('20605')"
                    >3Y</button>
                  </div>
                </div>
                <div v-if="kpiTrendUnavailable" class="kpi-trend-empty">No records found</div>
                <div v-else-if="!kpiTrendHasData" class="kpi-trend-empty">No records in this period</div>
                <div v-else class="kpi-trend-chart">
                  <apexchart
                    :key="kpiTrendChartKey"
                    type="line"
                    height="240"
                    :options="kpiTrendChartOptions"
                    :series="kpiTrendSeries"
                  />
                  <div class="kpi-trend-legend">
                    <span class="kpi-trend-legend-item">
                      <span class="kpi-trend-legend-line kpi-trend-legend-line--target"></span>
                      Target
                    </span>
                    <span class="kpi-trend-legend-item">
                      <span class="kpi-trend-legend-line kpi-trend-legend-line--achieved"></span>
                      Achieved
                    </span>
                  </div>
                </div>
              </div>
              <div v-for="(obj, index) in kpiResults" :key="index" class="kpi-history-result-block">
                <div class="kpi-history-table-wrap">
                <table class="kpi-history-table w-full">
                  <colgroup>
                    <col style="width: 23%" />
                    <col style="width: 27%" />
                    <col style="width: 23%" />
                    <col style="width: 27%" />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td class="kpi-history-date" colspan="4">{{ formatResultDate(getKpiResultGraphDate(obj)) }}</td>
                    </tr>
                    <tr>
                      <td class="headerData">Result</td>
                      <td class="text-center">
                        <div v-if="getKpiPassFail(obj) === 'Pass'" class="inline-flex items-center justify-center rounded h-5 w-28 text-xs font-semibold text-lightgreen bg-veryLowColor border-solid border border-lightgreen">
                          {{ getKpiPassFail(obj) }}
                        </div>
                        <div v-else-if="getKpiPassFail(obj) === 'Fail'" class="inline-flex items-center justify-center rounded h-5 w-28 text-xs font-semibold text-darkred bg-veryHighColor border-solid border border-darkred">
                          {{ getKpiPassFail(obj) }}
                        </div>
                        <div v-else-if="getKpiPassFail(obj) === 'No Instances'" class="inline-flex items-center justify-center rounded h-5 w-28 px-2.5 text-xs font-semibold text-gray bg-lightgrey border-solid border border-lightgrey">
                          {{ getKpiPassFail(obj) }}
                        </div>
                      </td>
                      <td class="headerData">Risk Severity</td>
                      <td class="text-center">
                        <template v-if="isKpiNoInstances(obj)">N/A</template>
                        <div v-else-if="obj.severity" class="inline-flex items-center justify-center rounded h-5 w-28 px-2.5 text-xs font-semibold" :class="colorRiskBg(obj.severity)">
                          <div :class="colorRiskText(obj.severity)">
                            {{ getSeverityLabel(obj.severity) }}
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td class="headerData">Target Type</td>
                      <td>{{ getKpiTargetTypeDisplay(obj) }}</td>
                      <td class="headerData">Condition</td>
                      <td class="text-center">{{ getKpiConditionDisplay(obj) }}</td>
                    </tr>
                    <tr>
                      <td class="headerData">Target</td>
                      <td>{{ getKpiTargetDisplay(obj) }}</td>
                      <td class="headerData">Achieved</td>
                      <td class="text-center">{{ getKpiActualDisplay(obj) }}</td>
                    </tr>
                    <tr>
                      <td class="headerData">Population</td>
                      <td>{{ obj.population }}</td>
                      <td class="headerData">Compliant</td>
                      <td class="text-center">{{ getKpiCompliantDisplay(obj) }}</td>
                    </tr>
                    <tr>
                      <td class="headerData">Comments</td>
                      <td colspan="3">{{ obj.comments }}</td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </Teleport>
    <!-- <loader v-if="showLoader" /> -->
  </div>
</template>
<style scoped>
.multiselect-tag {
  font-size: 12px;
  font-weight: 600;
}
.multiselect-tag.is-disabled {
  background-color: #059669 !important;
}
.multiselect.is-disabled {
  background-color: white !important;
}
.kpi-history-dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
}
.kpi-history-dialog-heading {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
}
.kpi-history-id {
  flex-shrink: 0;
  white-space: nowrap;
}
.kpi-history-sep {
  flex-shrink: 0;
}
.kpi-history-name {
  min-width: 0;
  flex: 1;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.kpi-history-dialog {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
  width: 70%;
  height: 80%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 0 1rem;
  flex-shrink: 0;
}
.kpi-history-dialog-title {
  flex-shrink: 0;
  padding-top: 1rem;
  padding-bottom: 8px;
  gap: 8px;
  min-width: 0;
}
.kpi-history-dialog-title button {
  flex-shrink: 0;
}
.kpi-history-dialog--empty {
  width: 28rem;
  height: auto;
  max-height: none;
}
.kpi-history-dialog--empty .kpi-history-body {
  flex: none;
  overflow: hidden;
  padding: 1rem;
  min-height: 72px;
  align-items: center;
  justify-content: center;
}
.kpi-history-dialog--empty .kpi-history-popup {
  flex: none;
  overflow: hidden;
  padding-right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kpi-history-empty-wrap {
  position: relative;
  width: 100%;
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kpi-history-empty-close {
  position: absolute;
  top: 0;
  right: 0;
}
.kpi-history-empty-msg {
  width: 100%;
  text-align: center;
  font-size: 14px;
}
.kpi-history-body {
  flex: 1 1 0%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  padding-top: 1rem;
  padding-bottom: 20px;
  padding-left: 1rem;
  padding-right: 8px;
}
.kpi-history-dialog .kpi-history-popup {
  font-size: 14px;
  flex: 1 1 0%;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 8px;
}
.kpi-history-popup .headerData {
  font-size: 12px;
}
.kpi-history-popup .header {
  font-size: 12px;
}
.kpi-trend-wrap {
  margin-bottom: 1.25rem;
  padding: 12px 14px 8px;
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
}
.kpi-trend-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}
.kpi-trend-title {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.kpi-trend-pills {
  display: flex;
  gap: 6px;
}
.kpi-trend-pill {
  border: 1px solid #D1D5DB;
  background: #fff;
  color: #4B5563;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  cursor: pointer;
  line-height: 1.4;
}
.kpi-trend-pill--active {
  background: #111827;
  border-color: #111827;
  color: #fff;
}
.kpi-trend-chart {
  width: 100%;
}
.kpi-trend-chart :deep(.apexcharts-svg),
.kpi-trend-chart :deep(.apexcharts-canvas) {
  overflow: visible;
}
.kpi-trend-chart :deep(.apexcharts-inner),
.kpi-trend-chart :deep(.apexcharts-graphical),
.kpi-trend-chart :deep(.apexcharts-series-markers-wrap) {
  overflow: visible;
  clip-path: none !important;
}
.kpi-trend-legend {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  padding: 4px 0 10px;
}
.kpi-trend-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}
.kpi-trend-legend-line {
  display: inline-block;
  width: 22px;
  height: 0;
  border-top-width: 3px;
}
.kpi-trend-legend-line--target {
  border-top-style: dotted;
  border-top-color: #16A34A;
}
.kpi-trend-legend-line--achieved {
  border-top-style: solid;
  border-top-color: #1976d2;
}
.kpi-trend-empty {
  text-align: center;
  font-size: 13px;
  padding: 2.5rem 0;
  color: #6b7280;
}
.kpi-history-result-block + .kpi-history-result-block {
  margin-top: 16px;
}
.kpi-history-table td.kpi-history-date {
  font-size: 14px;
  font-weight: 600;
  text-align: start;
  background-color: #e9e9e9;
}
.kpi-history-table {
  width: 100% !important;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
}
.kpi-history-table tr {
  border-top: none !important;
}
.kpi-history-table td {
  height: auto !important;
  padding: 6px !important;
  border-top: none !important;
  border-left: none !important;
  border-right: 1px solid #e9e9e9 !important;
  border-bottom: 1px solid #e9e9e9 !important;
  font-size: 14px;
  font-weight: 400;
  white-space: normal;
  vertical-align: middle;
  line-height: 1.25;
}
.kpi-history-table tr td:first-child {
  border-left: 1px solid #e9e9e9 !important;
}
.kpi-history-table tr:first-child td {
  border-top: 1px solid #e9e9e9 !important;
}
.kpi-history-table tr:first-child td:first-child {
  border-top-left-radius: 4px;
}
.kpi-history-table tr:first-child td:last-child {
  border-top-right-radius: 4px;
}
.kpi-history-table tr:last-child td:first-child {
  border-bottom-left-radius: 4px;
}
.kpi-history-table tr:last-child td:last-child {
  border-bottom-right-radius: 4px;
}
.kpi-history-table td.header {
  font-size: 12px;
  font-weight: 600;
}
.kpi-history-table td.headerData {
  font-size: 12px;
  font-weight: 600;
}
.bgError {
  background-color: rgba(255, 0, 0, 0.2);
}
.bgdarkred {
  background-color: rgba(192, 0, 0, 0.2);
}
.bgYellow {
  background-color: rgba(255, 153, 0, 0.2);
}
.bgLightGreen {
  background-color: rgba(0, 176, 80, 0.2);
}
.bgGreen {
  background-color: rgba(146, 208, 80, 0.2);
}
@media (max-width: 768px) {
  .kpi-history-dialog {
    width: 92%;
    height: 88%;
    margin: 0 0.5rem;
  }
  .kpi-trend-header {
    flex-wrap: wrap;
    gap: 8px;
  }
  .kpi-trend-title {
    font-size: 12px;
  }
  .kpi-trend-pill {
    font-size: 10px;
    padding: 3px 8px;
  }
  .kpi-trend-chart {
    overflow-x: auto;
  }
}
</style>
<style>
.kpi-history-popup::-webkit-scrollbar {
  width: 2px;
}
.kpi-history-popup::-webkit-scrollbar-track {
  background-color: #E9E9E9 !important;
  border-radius: 5px;
}
.kpi-history-popup::-webkit-scrollbar-thumb {
  background: #1976d2 !important;
  border-radius: 5px;
}
</style>
<script lang="ts">
import * as XLSX from 'xlsx';
// import loader from "../../components/loader.vue";
import _ from "lodash";
import moment from "moment";
import { defineComponent } from "vue";
import { mapGetters } from "vuex";
import { emitter, toast } from "@/main";
import tableData from "@/components/tableData.vue";
import toolTip from "@/components/toolTip.vue";
import VueApexCharts from "vue3-apexcharts";
// import Multiselect from '@vueform/multiselect/src/Multiselect';
export default defineComponent({
  data(): any {
    return {
      headers: ["_id","kpiId","KPI","CID","controlTitle","Team","weightage (%)","Target","kpiHealth","status"],

      isLoading: false,
      showLoader: false,

      showFilters: false,
      hideFormula: false,
      showPopup: false,
      columnObject: false,
      toolTipActive : false,
			toolTipText : '',
      dummyList: [],
      allKpisList: [],
      tableRow: [],
      allTableRows: [],
      departmentsList: [],
      controlDepartmentArr: [],
      naDepartments: "",

      kpiList: [],
      deptOptions: [],
      allKpis: {
        kpi_id: "",
        weightage: "",
        target:"",
        description: [],
      },
      kpiResults: [],
      kpiResultsLoading: false,
      kpiTrendTimePeriod: "",
      kpiTrendHasData: false,
      kpiTrendUnavailable: false,
      kpiTrendChartKey: 0,
      kpiTrendSeries: [] as any[],
      kpiTrendChartOptions: {
        chart: {
          type: "line",
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: "inherit",
          parentHeightOffset: 0
        },
        colors: ["#16A34A", "#a4a4a4", "#1976d2"],
        stroke: {
          curve: ["smooth", "smooth", "smooth"],
          width: [2, 0, 3],
          dashArray: [3, 0, 0]
        },
        markers: {
          size: [6, 6, 6],
          colors: ["#16A34A", "#a4a4a4", "#1976d2"],
          strokeColors: "#fff",
          strokeWidth: 2,
          hover: { size: 8 }
        },
        legend: {
          show: false
        },
        grid: {
          borderColor: "#E5E7EB",
          strokeDashArray: 3,
          padding: { left: 8, right: 12 }
        },
        xaxis: {
          type: "category",
          categories: [] as string[],
          title: { text: undefined },
          tickPlacement: "on",
          labels: {
            rotate: 0,
            hideOverlappingLabels: true,
            trim: false,
            showDuplicates: true,
            formatter: (value: string) => value,
            style: { fontSize: "11px", colors: "#6B7280" }
          },
          axisBorder: { color: "#E5E7EB" },
          axisTicks: { color: "#E5E7EB" }
        },
        yaxis: {
          min: 0,
          max: 100,
          tickAmount: 5,
          title: { text: undefined },
          labels: {
            formatter: (val: number) => `${val}%`,
            style: { fontSize: "11px", colors: "#6B7280" }
          }
        },
        dataLabels: { enabled: false },
        tooltip: {
          shared: true,
          intersect: false,
          y: { formatter: (val: number) => (val == null ? undefined : `${val}%`) }
        }
      },
      controlRecurringArray: [
        { label: "Daily", id: 1 },
        { label: "Weekly", id: 2 },
        { label: "Monthly", id: 3 },
        { label: "Quarterly", id: 4 },
        { label: "Half-Yearly", id: 5 },
        { label: "Yearly", id: 6 },
      ],
      dataLoading:true,
    };
  },
  components: {
    tableData,
    // loader,
    toolTip,
    apexchart: VueApexCharts
    // Multiselect
  },
  computed: {
    ...mapGetters({ dbSeverity: "dbSeverity" }),
  },
  async mounted() {
    this.dataLoading = true;
    await this.fetchDepartmentsList();
    await this.fetchAllKpisInfo();
    emitter.on("showKpisViewData", (isData: any) => {
      let comingId = isData.entityData._id;
      let showObj = this.kpiList.find((obj: any) => {
        return String(obj._id) === String(comingId);
      }) || isData.entityData;
      this.showEditPopUp(showObj, isData.indexx);
    });
    await this.makeTable();
    this.dataLoading = false;
    emitter.off('activiteToolTip');
		emitter.on('activiteToolTip', (data: any) => {
			this.toolTipActive = true;
			this.toolTipText = data.tipText
		});
		emitter.off('deActiviteToolTip');
		emitter.on('deActiviteToolTip', (data: any) => {
			this.toolTipActive = false;
			this.toolTipText = ''
		});
  },
  methods: {
    closeKpiHistory() {
      this.showPopup = false;
      this.kpiTrendHasData = false;
      this.kpiTrendUnavailable = false;
      this.kpiTrendSeries = [];
      this.kpiTrendTimePeriod = "";
    },
    getKpiTrendMonthCount() {
      if (this.kpiTrendTimePeriod === "20604") return 3;
      if (this.kpiTrendTimePeriod === "20603") return 6;
      if (this.kpiTrendTimePeriod === "20605") return 36;
      return 12;
    },
    getKpiTrendCutoffDate() {
      return moment().startOf("month").subtract(this.getKpiTrendMonthCount(), "months");
    },
    formatKpiTrendMonthLabel(date: any) {
      const parsed = moment(date);
      if (!parsed.isValid()) return "";
      return `${parsed.format("MMM")} '${parsed.format("YY")}`;
    },
    getKpiTrendPointOffset(valueCount: number, index: number) {
      if (valueCount <= 1) return 0;
      return ((index / Math.max(valueCount - 1, 1)) - 0.5) * 0.3;
    },
    getKpiResultGraphDate(result: any) {
      const value = result?.planner_task_completion_date || result?.plannerTaskCompletionDate;
      if (value == null || value === "") return null;
      return value;
    },
    parseKpiResultGraphDate(result: any) {
      const value = this.getKpiResultGraphDate(result);
      if (!value) return null;
      const parsed = moment(value);
      return parsed.isValid() ? parsed : null;
    },
    getKpiNoInstanceMonthKeys() {
      const keys = new Set<string>();
      (this.kpiResults || []).forEach((result: any) => {
        if (!this.isKpiNoInstances(result) && this.getKpiActualDisplay(result) !== "N/A") {
          return;
        }
        const parsed = this.parseKpiResultGraphDate(result);
        if (parsed) {
          keys.add(parsed.format("YYYY-MM"));
        }
      });
      return keys;
    },
    mergeKpiResultsIntoMonths(months: any[], results: any[]) {
      const byKey: Record<string, any> = {};
      months.forEach((month: any) => {
        byKey[month.sortKey] = month;
      });
      (results || []).forEach((result: any) => {
        const parsed = this.parseKpiResultGraphDate(result);
        if (!parsed) {
          return;
        }
        const month = byKey[parsed.format("YYYY-MM")];
        if (!month) {
          return;
        }
        const target = this.parseKpiTarget(result.kpiTarget ?? this.allKpis?.target);
        if (!isNaN(target)) {
          month.targetValues.push(target);
        }
        if (this.isKpiNoInstances(result) || this.getKpiActualDisplay(result) === "N/A") {
          return;
        }
        const achieved = this.getKpiActualValue(result);
        if (achieved != null && !isNaN(Number(achieved))) {
          month.achievedValues.push(Number(achieved));
        }
      });
    },
    trendPointSortKey(point: any) {
      if (point?.year && point?.month) {
        const parsed = moment(`${point.month} ${point.year}`, ["MMMM YYYY", "MMM YYYY"]);
        if (parsed.isValid()) return parsed.format("YYYY-MM");
      }
      if (point?.sortKey) return point.sortKey;
      if (point?.label) {
        const parsed = moment(String(point.label).replace("'", ""), ["MMM YYYY", "MMM YY", "MMMM YYYY"]);
        if (parsed.isValid()) return parsed.format("YYYY-MM");
      }
      return "";
    },
    buildCompleteTrendMonths() {
      const months: any[] = [];
      const cursor = this.getKpiTrendCutoffDate().clone();
      const end = moment().startOf("month");
      while (cursor.isSameOrBefore(end)) {
        months.push({
          sortKey: cursor.format("YYYY-MM"),
          label: this.formatKpiTrendMonthLabel(cursor),
          targetValues: [] as number[],
          achievedValues: [] as Array<number | null>,
        });
        cursor.add(1, "month");
      }
      return months;
    },
    mergeTrendValues(target: any, source: any) {
      const targetValues = Array.isArray(source.targetValues)
        ? source.targetValues.filter((value: any) => value != null && !isNaN(Number(value))).map(Number)
        : [];
      const achievedValues = Array.isArray(source.achievedValues)
        ? source.achievedValues.filter((value: any) => value != null && !isNaN(Number(value))).map(Number)
        : [];
      target.targetValues.push(...targetValues);
      if (!Array.isArray(source.achievedValues)) {
        return;
      }
      source.achievedValues.forEach((value: any) => {
        if (value == null) {
          target.achievedValues.push(null);
          return;
        }
        if (!isNaN(Number(value))) {
          target.achievedValues.push(Number(value));
        }
      });
    },
    mergeAnalyticsIntoMonths(months: any[], kpiTrend: any) {
      const byKey: Record<string, any> = {};
      months.forEach((month: any) => {
        byKey[month.sortKey] = month;
      });
      (kpiTrend?.monthWiseTargetAchieved || []).forEach((point: any) => {
        const key = this.trendPointSortKey(point);
        if (byKey[key]) {
          this.mergeTrendValues(byKey[key], point);
        }
      });
    },
    applyKpiTargetAchievedChart(kpiTrend: any) {
      this.kpiTrendUnavailable = false;
      const points = (kpiTrend?.monthWiseTargetAchieved || []).map((point: any) => {
        const sortKey = point.sortKey || this.trendPointSortKey(point);
        const rawAchievedValues = Array.isArray(point.achievedValues) ? point.achievedValues : [];
        const targetValues = Array.isArray(point.targetValues)
          ? point.targetValues.filter((value: any) => value != null && !isNaN(Number(value))).map(Number)
          : [];
        const achievedValues = rawAchievedValues
          .filter((value: any) => value != null && !isNaN(Number(value)))
          .map(Number);
        return {
          sortKey,
          label: point.label || this.formatKpiTrendMonthLabel(moment(sortKey, "YYYY-MM")),
          targetValues,
          achievedValues,
          isNoInstance: rawAchievedValues.some((value: any) => value == null),
        };
      });
      const hasData = points.some(
        (point: any) =>
          point.targetValues.length > 0 ||
          point.achievedValues.length > 0 ||
          point.isNoInstance
      );
      if (!hasData) {
        this.kpiTrendHasData = false;
        this.kpiTrendSeries = [];
        return;
      }
      const categories = points.map((point: any) => point.label);
      const targetData: Array<{ x: number; y: number }> = [];
      const achievedRuns: Array<Array<{ x: number; y: number }>> = [];
      let currentAchievedRun: Array<{ x: number; y: number }> = [];
      const noInstanceData: Array<{ x: number; y: number } | null> = [];
      const flushAchievedRun = () => {
        if (currentAchievedRun.length) {
          achievedRuns.push(currentAchievedRun);
          currentAchievedRun = [];
        }
      };
      points.forEach((point: any, monthIndex: number) => {
        point.targetValues.forEach((value: number, index: number) => {
          targetData.push({
            x: monthIndex + this.getKpiTrendPointOffset(point.targetValues.length, index),
            y: value,
          });
        });
        if (point.isNoInstance) {
          flushAchievedRun();
          if (noInstanceData.length) {
            noInstanceData.push(null);
          }
          noInstanceData.push({ x: monthIndex, y: 0 });
          return;
        }
        point.achievedValues.forEach((value: number, index: number) => {
          currentAchievedRun.push({
            x: monthIndex + this.getKpiTrendPointOffset(point.achievedValues.length, index),
            y: value,
          });
        });
      });
      flushAchievedRun();
      const hasNoInstanceSeries = noInstanceData.some((point) => point != null);
      const rotateLabels = categories.length > 14;
      const lastX = Math.max(categories.length - 1, 0);
      const achievedCount = achievedRuns.length;
      const noInstanceSeriesIndex = hasNoInstanceSeries ? 1 : -1;
      const achievedColors = Array(achievedCount).fill("#1976d2");
      const chartColors = [
        "#16A34A",
        ...(hasNoInstanceSeries ? ["#a4a4a4"] : []),
        ...achievedColors,
      ];
      const isNumberKpi = this.getKpiTargetType(this.allKpis) === "number";
      const formatTrendY = (val: number) => {
        if (val == null) return undefined;
        return isNumberKpi ? String(Math.round(val)) : `${val}%`;
      };
      let yAxisMax = 100;
      if (isNumberKpi) {
        const yValues = [
          ...targetData.map((point) => point.y),
          ...achievedRuns.flatMap((run) => run.map((point) => point.y)),
        ].filter((value) => Number.isFinite(value));
        const dataMax = yValues.length ? Math.max(...yValues, 0) : 0;
        yAxisMax = dataMax === 0 ? 10 : Math.ceil(dataMax * 1.1);
      }
      this.kpiTrendHasData = true;
      this.kpiTrendChartKey += 1;
      this.kpiTrendChartOptions = {
        ...this.kpiTrendChartOptions,
        colors: chartColors,
        stroke: {
          curve: [
            "smooth",
            ...(hasNoInstanceSeries ? ["smooth"] : []),
            ...Array(achievedCount).fill("smooth"),
          ],
          width: [
            2,
            ...(hasNoInstanceSeries ? [0] : []),
            ...Array(achievedCount).fill(3),
          ],
          dashArray: [
            3,
            ...(hasNoInstanceSeries ? [0] : []),
            ...Array(achievedCount).fill(0),
          ]
        },
        markers: {
          size: [
            6,
            ...(hasNoInstanceSeries ? [6] : []),
            ...Array(achievedCount).fill(6),
          ],
          colors: chartColors,
          strokeColors: "#fff",
          strokeWidth: 2,
          hover: { size: 8 }
        },
        grid: {
          borderColor: "#E5E7EB",
          strokeDashArray: 3,
          padding: { left: 10, right: 24, top: 8, bottom: rotateLabels ? 12 : 4 }
        },
        xaxis: {
          type: "numeric",
          min: 0,
          max: lastX === 0 ? 1 : lastX + 0.4,
          tickAmount: Math.max(lastX, 1),
          stepSize: 1,
          title: { text: undefined },
          labels: {
            rotate: rotateLabels ? -45 : 0,
            rotateAlways: rotateLabels,
            hideOverlappingLabels: true,
            showDuplicates: false,
            maxHeight: rotateLabels ? 80 : 40,
            formatter: (value: string) => {
              const numeric = Number(value);
              const index = Math.round(numeric);
              if (!Number.isFinite(numeric) || index < 0 || index > lastX) {
                return "";
              }
              if (Math.abs(numeric - index) > 0.2) {
                return "";
              }
              return categories[index] || "";
            },
            style: {
              fontSize: rotateLabels ? "9px" : "11px",
              colors: "#6B7280"
            }
          },
          axisBorder: { color: "#E5E7EB" },
          axisTicks: { color: "#E5E7EB" }
        },
        yaxis: {
          min: 0,
          max: yAxisMax,
          tickAmount: 5,
          forceNiceScale: isNumberKpi,
          title: { text: undefined },
          labels: {
            formatter: (val: number) => formatTrendY(val) || "",
            style: { fontSize: "11px", colors: "#6B7280" }
          }
        },
        tooltip: {
          shared: false,
          intersect: true,
          x: {
            formatter: (value: number) => {
              const index = Math.round(Number(value));
              return categories[index] || "";
            }
          },
          y: {
            formatter: (val: number, opts: any) => {
              if (val == null) return undefined;
              if (hasNoInstanceSeries && opts?.seriesIndex === noInstanceSeriesIndex) {
                return "No Instances";
              }
              return formatTrendY(val);
            }
          }
        },
        responsive: [
          {
            breakpoint: 1024,
            options: {
              xaxis: {
                labels: {
                  rotate: -45,
                  rotateAlways: true,
                  hideOverlappingLabels: true,
                  maxHeight: 72,
                  style: { fontSize: "9px", colors: "#6B7280" }
                }
              },
              grid: {
                padding: { left: 8, right: 8, bottom: 16 }
              }
            }
          },
          {
            breakpoint: 768,
            options: {
              xaxis: {
                labels: {
                  rotate: -45,
                  rotateAlways: true,
                  hideOverlappingLabels: true,
                  maxHeight: 80,
                  style: { fontSize: "8px", colors: "#6B7280" }
                }
              },
              grid: {
                padding: { left: 4, right: 6, bottom: 20 }
              }
            }
          }
        ]
      };
      const targetSeriesData = targetData.length > 2
        ? [targetData[0], targetData[targetData.length - 1]]
        : targetData;
      this.kpiTrendSeries = [
        {
          name: "Target",
          data: targetSeriesData
        },
        ...(hasNoInstanceSeries
          ? [{
              name: "Achieved",
              data: noInstanceData
            }]
          : []),
        ...achievedRuns.map((data) => ({
          name: "Achieved",
          data
        })),
      ];
    },
    setKpiTrendPeriod(period: string) {
      if (this.kpiTrendTimePeriod === period) {
        return;
      }
      this.kpiTrendTimePeriod = period;
      this.fetchKpiTargetAchievedTrend();
    },
    getKpiTrendApiDateRange() {
      const startDate = this.getKpiTrendCutoffDate().toISOString();
      const endDate = moment().endOf("day").toISOString();
      return { startDate, endDate };
    },
    markKpiTrendUnavailable() {
      this.kpiTrendHasData = false;
      this.kpiTrendUnavailable = true;
      this.kpiTrendSeries = [];
    },
    async fetchKpiTargetAchievedTrend() {
      if (!this.allKpis?._id) {
        this.markKpiTrendUnavailable();
        return;
      }
      try {
        const kpiFilters: Record<string, any> = {
          kpiId: String(this.allKpis._id),
        };
        if (this.kpiTrendTimePeriod) {
          kpiFilters.timePeriod = Number(this.kpiTrendTimePeriod);
        }
        const result = await this.$http.post(
          `${process.env.VUE_APP_ANALYTICS_API}/monitor/kpis/dashboard`,
          { kpiFilters }
        );
        const kpiTrend = result?.data?.KPI_Target_Achieved?.[0];
        const monthWise = kpiTrend?.monthWiseTargetAchieved;
        if (!Array.isArray(monthWise) || monthWise.length === 0) {
          this.markKpiTrendUnavailable();
        return;
      }
      const months = this.buildCompleteTrendMonths();
        this.mergeAnalyticsIntoMonths(months, kpiTrend);
      this.applyKpiTargetAchievedChart({ monthWiseTargetAchieved: months });
      } catch {
        this.markKpiTrendUnavailable();
      }
    },
    async showEditPopUp(obj: any, _index: any) {
      if (!obj) {
        return;
      }
      this.allKpis = { ...obj };
      this.hideFormula = true;
      this.kpiResults = [];
      this.kpiResultsLoading = true;
      this.kpiTrendTimePeriod = "";
      this.kpiTrendHasData = false;
      this.kpiTrendUnavailable = false;
      this.kpiTrendSeries = [];
      const trendPromise = this.fetchKpiTargetAchievedTrend();
      try {
        if (!obj.controlId || !obj._id) {
          this.kpiResults = [];
        } else {
        const result = await this.$http.get(
          `${process.env.VUE_APP_CONTROLS_API}/api/org/control/${obj.controlId}/kpi/${obj._id}/results`
        );
        this.kpiResults = (result.data || []).map((doc: any) => {
          const population = Number(doc.population);
          const compliant = Number(doc.compliant);
          let severity = doc.risk_severity ?? doc.riskSeverity ?? doc.severity;
          if (population === 0) {
            severity = null;
          }
          return {
            ...doc,
            actualOutput: doc.compliant,
            totalPopulation: doc.population,
            severity,
          };
        });
        }
      } catch (e) {
        this.kpiResults = [];
        toast.error("error", {
          timeout: 1000,
          closeOnClick: true,
          closeButton: "button",
          icon: true,
        });
      }
      this.kpiResultsLoading = false;
      await trendPromise;
      this.showPopup = true;
    },
    filterOpen() {
      if (this.columnObject == false) {
        this.showFilters = !this.showFilters;
        emitter.emit("visible", this.showFilters);
      }
    },
    async fetchAllKpisInfo() {
      try {
        await this.$http
          .get(`${process.env.VUE_APP_CONTROLS_API}/api/org/kpis/getall`)
          .then((result: { data: any }) => {
            this.isLoading = true;
            this.showLoader = true;
            this.allKpisList = result.data;
            _.each(
              this.allKpisList,
              (kpi: {
                kpis: any;
                ccid: any;
                controlTitle: any;
                departments: any;
              }) => {
                if (kpi.kpis) {
                  _.each(
                    kpi.kpis,
                    (kpi: {
                      ccid: any;
                      targetTimePeriod: any;
                      reportingFrequency: any;
                      controlTitle: any;
                      departments: any;
                    }) => {
                      // kpi.departments = kpi.departments;
                      this.kpiList.push(kpi);
                    }
                  );
                  this.dummyList = this.kpiList;
                }
              }
            );
          });
      } catch (e) {
        toast.error("error", {
          timeout: 1000,
          closeOnClick: true,
          closeButton: "button",
          icon: true,
        });
      }
      this.showLoader = false;
    },
    async fetchDepartmentsList() {
      (this.showLoader = true),
        //  (this.isLoading = false);
        await this.$http
          .get(`${process.env.VUE_APP_ORG_API}/departments/get`)
          .then((res: { data: any }) => {
            this.isLoading = true;
            this.showLoader = false;
            this.departmentsList = res.data;
          })
          .catch((error: any) => {
            this.isLoading = false;
            (this.showLoader = false),
              toast.error(`Save failed with ${error}`, {
                timeout: 1000,
                closeOnClick: true,
                closeButton: "button",
                icon: true,
              });
          });
    },
    buildKpiHealthTooltip(kpi: any) {
      const kpiResult = kpi.kpiResult ? String(kpi.kpiResult) : '';
      let dateStr = '';
      if (kpi.kpiHealthDate) {
        const parsed = moment(kpi.kpiHealthDate);
        if (parsed.isValid()) {
          dateStr = parsed.format("DD-MM-YYYY");
        }
      }
      if (kpiResult && dateStr) {
        return `${kpiResult} · ${dateStr}`;
      }
      return kpiResult || dateStr || '';
    },
    makeTable() {
	  this.tableRow = [];
      this.kpiList.forEach((kpi: any) => {
        this.departmentsList.forEach((eachdepartment: any) => {
          if (kpi.department == eachdepartment._id) {
            kpi.department = eachdepartment.displayName;
          }
        });
        kpi.isformula = "A/B";
        this.deptOptions = this.departmentsList.map((item: any) => {
          return { value: item._id, label: item.displayName };
        });
        let finalKPIObj: any = {
          _id: kpi._id,
          Team: kpi.department == undefined || kpi.department == '' ? "N/A" : kpi.department ,
          // number: kpi.controlNumber != undefined ? kpi.controlNumber : "N/A",
          controlTitle: kpi.controlTitle != undefined ? kpi.controlTitle : "N/A",
          CID: kpi.ccid != undefined ? kpi.ccid : "N/A",
          controlId: kpi.controlId != undefined ? kpi.controlId : "",
          "kpiId": kpi.kpi_id != undefined ? kpi.kpi_id : "N/A",
          "KPI": kpi.description != undefined ? kpi.description : "N/A",
          "weightage (%)": parseInt(kpi.weightage),
          "Target": this.formatKpiTargetForTable(kpi),
          "kpiHealth": kpi.kpiHealth != undefined && kpi.kpiHealth != '' ? kpi.kpiHealth : "N/A",
          "kpiHealthTooltip": this.buildKpiHealthTooltip(kpi),
          "status":kpi.isActive && kpi.isActive==true?1:2
          // 'Target (%)': parseInt(kpi.target),
          // metrics: kpi.metrics,
          // 'Target Time Period': Number(kpi.targetTimePeriod) ? parseInt(kpi.targetTimePeriod) : kpi.targetTimePeriod,
          // 'Reporting Frequency': Number(kpi.reportingFrequency) ? parseInt(kpi.reportingFrequency) : kpi.reportingFrequency,
          // isformula: kpi.isformula
        };
        this.tableRow.push(finalKPIObj);
      });
      this.allTableRows = [...this.tableRow];
      this.applyDashboardKpiFilter();
      // if (this.tableRow.length == 0) {
      //   let finalKPIObj = {
      //     departments: "",
      //     CCID: "",
      //     // "KPI ID": '',
      //     "KPI Name": "",
      //     "weightage (%)": "",
      //     // 'Target (%)': '',
      //     // 'Target Time Period': '',
      //     // 'Reporting Frequency': ''
      //   };
      //   this.tableRow.push(finalKPIObj);
      // }
      if (this.kpiList && this.kpiList.length == 0) {
        this.columnObject = true;
      } else {
        this.columnObject = false;
      }
    },
    applyDashboardKpiFilter() {
      const kpi = String(this.$route.query.kpi || '');
      if (!kpi || kpi !== 'kpiPassRate') {
        this.tableRow = [...this.allTableRows];
        return;
      }
      const passingHealth = new Set([4, 5, '4', '5', 'Pass']);
      const hasHealth = this.allTableRows.some((row: any) => row.kpiHealth !== undefined && row.kpiHealth !== 'N/A' && row.kpiHealth !== '');
      if (!hasHealth) {
        this.tableRow = [...this.allTableRows];
        return;
      }
      this.tableRow = this.allTableRows.filter((row: any) => passingHealth.has(row.kpiHealth));
    },
    getSeverityLabel(cameId: any) {
      if (cameId == null || cameId === "" || cameId === "N/A") {
        return "N/A";
      }
      let labelObj = this.dbSeverity.find((l: any) => {
        return l.value == cameId;
      });
      if (labelObj == undefined) {
        return "-";
      } else {
        return labelObj.key;
      }
    },
    parseKpiTarget(target: any) {
      if (target == null || target === "") return NaN;
      return Number(String(target).replace("%", "").trim());
    },
    formatKpiTargetForTable(kpi: any) {
      if (kpi?.target == null || kpi.target === "") return "N/A";
      const targetType = kpi.targetType || "percentage";
      const numeric = this.parseKpiTarget(kpi.target);
      if (isNaN(numeric)) return "N/A";
      if (targetType === "number") {
        return String(numeric);
      }
      return `${numeric}%`;
    },
    getKpiTargetType(obj: any) {
      return obj?.targetType || this.allKpis?.targetType || "percentage";
    },
    getKpiTargetTypeDisplay(obj: any) {
      const raw =obj?.targetType      
      if (raw === "number") return "Number";
      if (raw === "percentage") return "Percentage";
      return "N/A";
    },
    getKpiConditionDisplay(obj: any) {
      const raw = obj?.kpiCondition      
      if (raw === "gte") return "'≥' Greater than or Equal to";
      if (raw === "lte") return "'≤' Less than or Equal to";
      if (raw === "gt") return "'>' Greater than";
      if (raw === "lt") return "'<' Less than";
      if (raw === "eq") return "'=' Equal to";
      return "N/A";
    },
    getKpiActualValue(obj: any) {
      const targetType = this.getKpiTargetType(obj);
      if (obj?.kpiResult != null && obj.kpiResult !== "") {
        const stored = Number(String(obj.kpiResult).replace("%", "").trim());
        if (!isNaN(stored)) return stored;
      }
      const population = Number(obj?.totalPopulation ?? obj?.population);
      const compliant = Number(obj?.actualOutput ?? obj?.compliant);
      if (targetType === "number") {
        if (isNaN(population)) return null;
        return population;
      }
      if (!population || isNaN(compliant)) return null;
      return Math.round((compliant / population) * 100);
    },
    getKpiActualDisplay(obj: any) {
      const population = Number(obj?.totalPopulation ?? obj?.population);
      if (!isNaN(population) && population === 0) return "N/A";
      const targetType = this.getKpiTargetType(obj);
      const storedResult = obj?.kpiResult_Value ?? obj?.kpiResult;
      if (storedResult != null && storedResult !== "") {
        if (storedResult === "N/A") return "N/A";
        if (targetType === "number") {
          return String(storedResult).replace("%", "").trim();
        }
        return String(storedResult).includes("%") ? storedResult : `${storedResult}%`;
      }
      const actual = this.getKpiActualValue(obj);
      if (actual == null) return "";
      return targetType === "number" ? `${actual}` : `${actual}%`;
    },
    getKpiCompliantDisplay(obj: any) {
      const population = Number(obj?.totalPopulation ?? obj?.population);
      if (!isNaN(population) && population === 0) return "N/A";
      const compliant = obj?.actualOutput ?? obj?.compliant;
      return compliant == null || compliant === "" ? "" : compliant;
    },
    isKpiNoInstances(obj: any) {
      const population = Number(obj?.totalPopulation ?? obj?.population);
      return (!isNaN(population) && population === 0) || obj?.result === "No Instances";
    },
    getKpiTargetDisplay(obj: any) {
      const targetType = this.getKpiTargetType(obj);
      const raw = obj?.kpiTarget ?? this.allKpis?.target;
      if (raw == null || raw === "") return "N/A";
      if (targetType === "number") {
        return String(raw).replace("%", "").trim();
      }
      return String(raw).includes("%") ? raw : `${raw}%`;
    },
    getKpiPassFail(obj: any) {
      const population = Number(obj?.totalPopulation ?? obj?.population);
      if ((!isNaN(population) && population === 0) || obj?.result === "No Instances") {
        return "No Instances";
      }
      if (obj?.result === "Pass" || obj?.result === "Fail") {
        return obj.result;
      }
      const actual = this.getKpiActualValue(obj);
      const target = this.parseKpiTarget(obj?.kpiTarget ?? this.allKpis?.target);
      if (actual == null || isNaN(target)) return "";
      const kpiCondition = this.allKpis?.kpiCondition || this.allKpis?.targetOperator || this.allKpis?.logic || obj?.kpiCondition || obj?.targetOperator || obj?.logic;
      let passes = false;
      if (kpiCondition === "lt") passes = actual < target;
      else if (kpiCondition === "lte") passes = actual <= target;
      else if (kpiCondition === "gt") passes = actual > target;
      else if (kpiCondition === "eq") passes = actual === target;
      else passes = actual >= target; // gte or unknown/empty default
      return passes ? "Pass" : "Fail";
    },
    riskSeverity(value: number) {
      if (value <= 5) {
        return 1;
      } else if (value > 5 && value <= 10) {
        return 2;
      } else if (value > 10 && value <= 15) {
        return 3;
      } else if (value > 15 && value <= 20) {
        return 4;
      } else if (value > 20 && value <= 25) {
        return 5;
      }
    },
    formatResultDate(time: any) {
      if (!time) return "N/A";
      const parsed = moment(time);
      if (!parsed.isValid()) return "N/A";
      return parsed.format("D MMM YYYY");
    },
    colorRiskText(severity: any) {
      if (severity == 1) {
        return ["text-lightgreen text-xs font-semibold"];
      } else if (severity == 2) {
        return ["text-green text-xs font-semibold"];
      } else if (severity == 3) {
        return ["text-yellow text-xs font-semibold"];
      } else if (severity == 4) {
        return ["text-error text-xs font-semibold"];
      } else if (severity == 5) {
        return ["text-darkred text-xs font-semibold"];
      }
    },
    colorRiskBg(severity: any) {
      if (severity == 1) {
        return ["bgLightGreen border-solid border border-lightgreen"];
      } else if (severity == 2) {
        return ["bgGreen border-solid border border-green"];
      } else if (severity == 3) {
        return ["bgYellow border-solid border border-yellow"];
      } else if (severity == 4) {
        return ["bgError border-solid border border-error"];
      } else if (severity == 5) {
        return ["bgdarkred border-solid border border-darkred"];
      }
    },
  },
});
</script>